// filepath: admin-dashboard/src/context/AuthContext.tsx
import React, { createContext, useContext, useCallback, useState } from 'react';
import {
  authService,
  LoginCredentials,
  UserProfile,
} from '../services/auth/authService';
import { runVideoUrlSweep } from '../services/videoUrl/videoUrlSweep';

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserProfile | null;
  login: (credentials: LoginCredentials) => Promise<UserProfile>;
  logout: () => void;
  isAdmin: boolean;
  mustChangePassword: boolean;
  clearMustChangePassword: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialise from whatever is already in localStorage (survives refresh)
  const [isAuthenticated, setIsAuthenticated] = useState(
    authService.isAuthenticated()
  );
  const [user, setUser] = useState<UserProfile | null>(authService.getUser());

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<UserProfile> => {
      const profile = await authService.login(credentials);
      setIsAuthenticated(true);
      setUser(profile);
      // Fire-and-forget post-login URL sanitization sweep across all
      // collections + locales. Non-blocking: failures are logged only.
      try {
        const sweepResult = runVideoUrlSweep();
        if (sweepResult && typeof sweepResult.then === 'function') {
          sweepResult
            .then((report) => {
              if (report.totalUpdated > 0) {
                // eslint-disable-next-line no-console
                console.info(
                  `[videoUrl sweep] sanitized ${report.totalUpdated}/${report.totalScanned} item(s)`,
                  report.details
                );
              }
              if (report.failures.length > 0) {
                // eslint-disable-next-line no-console
                console.warn('[videoUrl sweep] failures:', report.failures);
              }
            })
            .catch((err) => {
              // eslint-disable-next-line no-console
              console.error('[videoUrl sweep] unexpected error', err);
            });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[videoUrl sweep] failed to start', err);
      }
      return profile;
    },
    []
  );

  const logout = useCallback(() => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  const clearMustChangePassword = useCallback(() => {
    authService.clearMustChangePassword();
    setUser((prev) => (prev ? { ...prev, must_change_password: false } : prev));
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    login,
    logout,
    isAdmin: user?.role === 'admin',
    mustChangePassword: user?.must_change_password ?? false,
    clearMustChangePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
